import { getDemoSoc, getDemoGeneration, getDemoChargerPower, getDemoHvacMode } from '@/lib/demoData';
import { currentSupabaseEnv } from '@/lib/supabase';
import type { Unit } from '@/types';

/**
 * Non-component helpers for SystemStatusIcons, split out so the component file
 * exports only the component (react-refresh/only-export-components).
 */
export type SystemType = 'solar' | 'charger' | 'hvac' | 'starlink';

/**
 * Which source is charging the pack, from fleet-health `charge_source` (server-
 * classified; CLOSED enum). null = not charging OR no current reading — the wire
 * doesn't distinguish them, so the null tooltip must not assert either one.
 */
export type ChargeSource = 'solar' | 'shore' | 'shore_likely';

/**
 * The charging-slot glyph for a charge source: sun for solar, plug for shore.
 * Charging from shore lights the plug amber like the charging sun (Eric, 2026-10-30: "plug needs
 * to be lit yellow like the sun when it is charging up"); never an alert colour (coral/destructive
 * is reserved for negative). A "likely" shore reading is an inference, so it renders dimmed.
 */
export function chargeSlotAppearance(source: ChargeSource | null): {
  icon: 'sun' | 'plug';
  colorClass: string;
  title: string;
} {
  switch (source) {
    case 'solar':
      return { icon: 'sun', colorClass: getSystemIconColor('solar', true), title: 'Charging: solar' };
    case 'shore':
      return { icon: 'plug', colorClass: getSystemIconColor('solar', true), title: 'Charging: shore power' };
    case 'shore_likely':
      return { icon: 'plug', colorClass: 'text-muted-foreground', title: 'Likely plugged in (shore power)' };
    default:
      return { icon: 'sun', colorClass: getSystemIconColor('solar', false), title: 'Not charging, or no current reading' };
  }
}

/**
 * Returns the active or muted CSS color class for a given system type.
 * Shared so the settings edit drawer and map cards use the same colors.
 */
export function getSystemIconColor(system: SystemType, isActive: boolean): string {
  if (!isActive) return 'text-muted-foreground/30';
  switch (system) {
    case 'solar': return 'text-amber-400';
    case 'charger': return 'text-blue-400';
    case 'hvac': return 'text-cyan-400';
    case 'starlink': return 'text-green-400';
  }
}

/**
 * Resolves system metric values, applying demo fallbacks when in demo mode
 * and real data is missing/zero.
 */
export function resolveSystemMetrics(unit: Unit) {
  const rawGeneration = unit.systems?.solar?.generation ?? 0;
  const rawBattery = unit.systems?.solar?.battery ?? 0;
  const rawChargerPower = unit.systems?.charger?.powerOut ?? 0;
  const rawHvacMode = unit.systems?.hvac?.mode ?? 'off';

  const isDemo = currentSupabaseEnv === 'demo';

  return {
    solarGeneration: isDemo && rawGeneration === 0 ? getDemoGeneration(unit.id) : rawGeneration,
    batteryLevel: isDemo && rawBattery === 0 ? getDemoSoc(unit.id) : rawBattery,
    chargerPower: isDemo && rawChargerPower === 0 ? getDemoChargerPower(unit.id) : rawChargerPower,
    hvacMode: isDemo && rawHvacMode === 'off' ? getDemoHvacMode(unit.id) : rawHvacMode,
    starlinkStatus: unit.systems?.starlink?.connectionStatus ?? 'offline',
    starlinkDownload: unit.systems?.starlink?.currentMetrics?.downloadSpeed ?? 0,
    chargerStatus: unit.systems?.charger?.status,
    hvacTemp: unit.systems?.hvac?.temperature,
  };
}
